'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useTransition } from 'react';

import { Button } from '@/components/atoms/Button';
import { captureException } from '@/lib/analytics';

interface VenueDetailErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * /venue/<slug> error boundary (WEB-1085).
 *
 * `fetchVenueDetail` REJECTS on a timeout, network error or malformed body —
 * none of which says the venue is missing — so this is what a visitor sees
 * during an upstream stall instead of a cached 404. It lives inside `(city)`
 * so the city chrome stays on screen, which the root `app/error.tsx` (outside
 * the `(city)` layout) cannot offer.
 *
 * Retry has to re-run the SERVER render: a bare `reset()` only re-mounts the
 * client boundary and would replay the same failed RSC payload. Refreshing
 * the router inside a transition re-requests the segment, and `reset()` then
 * clears the boundary once the fresh payload lands. Nothing was cached for
 * the failure, so the refresh genuinely reaches the API again.
 *
 * An error boundary does not commit a streaming 200 the way `loading.tsx`
 * does, so the WEB-915 real-404 contract for `notFound()` is unaffected.
 */
export default function VenueDetailError({ error, reset }: VenueDetailErrorProps) {
  const router = useRouter();
  const [isRetrying, startTransition] = useTransition();

  useEffect(() => {
    // A boundary-caught render error never reaches the SDK's
    // unhandled-exception autocapture; forward it like `app/error.tsx` does.
    captureException(error, { boundary: 'venue/[slug]/error' });
  }, [error]);

  const retry = (): void => {
    startTransition(() => {
      router.refresh();
      reset();
    });
  };

  return (
    <div className="container mx-auto px-4 py-16 text-center">
      <h1 className="text-2xl font-bold mb-2">This page didn&apos;t load</h1>
      <p className="text-muted-foreground mb-6">
        The venue didn&apos;t load. Try again.
        {error.digest && (
          <span className="block mt-1 text-xs opacity-50">Error ID: {error.digest}</span>
        )}
      </p>
      <Button variant="accent" size="lg" onClick={retry} disabled={isRetrying}>
        {isRetrying ? 'Retrying…' : 'Try again'}
      </Button>
    </div>
  );
}
